import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { Card } from '@components/primitives/Card.js'
import { Alert } from '@components/primitives/Alert.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { calcDetailPath } from '@utils/constants.js'

interface CalcItem {
  id: string
  rf: string
  title: string
  desc: string
}

interface CalcGrupo {
  moduloId: string
  modulo: string
  rf: string
  items: CalcItem[]
}

const CALC_GRUPOS: CalcGrupo[] = [
  {
    moduloId: 'terreno',
    modulo: 'Terreno',
    rf: 'RF-08A',
    items: [
      {
        id: 'topografia',
        rf: '08A.1',
        title: 'Diagnóstico de Topografia',
        desc: 'Talude · escalonamento · deck · rebaixo e impacto no custo.',
      },
      {
        id: 'corte-aterro',
        rf: '08A.2',
        title: 'Corte e Aterro',
        desc: 'Volume de terra a movimentar e custo por m³.',
      },
      {
        id: 'insolacao',
        rf: '08A.3',
        title: 'Análise de Insolação',
        desc: 'Orientação solar das fachadas e horas de sol por face.',
      },
      {
        id: 'sondagem',
        rf: '08A.4',
        title: 'Custo de Sondagem SPT',
        desc: 'Número de furos, profundidade e perfil esperado do solo.',
      },
    ],
  },
  {
    moduloId: 'sistemas-construtivos',
    modulo: 'Sistemas Construtivos',
    rf: 'RF-08B',
    items: [
      {
        id: 'sistemas',
        rf: '08B.1',
        title: 'Comparador de Sistemas',
        desc: 'Alvenaria · Steel Frame · ICF · metálica lado a lado.',
      },
      {
        id: 'espessura',
        rf: '08B.2',
        title: 'Espessura de Parede',
        desc: 'Impacto da espessura na área útil, custo e isolamento.',
      },
      {
        id: 'vaos',
        rf: '08B.3',
        title: 'Vãos Livres',
        desc: 'Viga padrão · reforçada · protendida · treliça por vão.',
      },
    ],
  },
  {
    moduloId: 'projetos',
    modulo: 'Projetos',
    rf: 'RF-08C',
    items: [
      {
        id: 'casca',
        rf: '08C.1',
        title: 'Fator Casca',
        desc: 'Eficiência geométrica da planta. Quanto mais próximo de 1, melhor.',
      },
      {
        id: 'comodo',
        rf: '08C.2',
        title: 'Custo por Cômodo',
        desc: 'Heatmap mostrando o custo/m² de cada cômodo da casa.',
      },
      {
        id: 'otimizador',
        rf: '08C.3',
        title: 'Otimizador de Áreas',
        desc: 'Distribua íntimo × social × serviço × circulação com sliders.',
      },
      {
        id: 'aberturas',
        rf: '08C.4',
        title: 'Aberturas/Esquadrias',
        desc: '% de vidro na fachada vs ganho de luz e perda térmica.',
      },
      {
        id: 'pe-direito',
        rf: '08C.5',
        title: 'Pé-direito',
        desc: 'Altura ideal vs custo extra e conforto térmico.',
      },
    ],
  },
  {
    moduloId: 'planejamento-financeiro',
    modulo: 'Planejamento Financeiro',
    rf: 'RF-08D',
    items: [
      {
        id: 'viabilidade',
        rf: '08D.1',
        title: 'Simulador de Viabilidade',
        desc: 'Orçamento estimado por fase a partir da área + padrão + região.',
      },
      {
        id: 'curva-s',
        rf: '08D.2',
        title: 'Curva S de Desembolso',
        desc: 'Fluxo de caixa mensal previsto, com pico financeiro.',
      },
      {
        id: 'reserva',
        rf: '08D.3',
        title: 'Reserva de Contingência',
        desc: '% recomendado para imprevistos, ajustado pelo risco.',
      },
      {
        id: 'financiamento',
        rf: '08D.4',
        title: 'Financiamento',
        desc: 'SAC × PRICE: parcela inicial, total de juros, comparativo.',
      },
      {
        id: 'inflacao',
        rf: '08D.5',
        title: 'Inflação vs Cronograma',
        desc: 'Quanto o INCC consome do orçamento ao longo da obra.',
      },
    ],
  },
  {
    moduloId: 'mao-de-obra',
    modulo: 'Mão de Obra',
    rf: 'RF-08E',
    items: [
      {
        id: 'contratacao',
        rf: '08E.1',
        title: 'Tipos de Contratação',
        desc: 'Empreitada Total × Parcial × Administração lado a lado.',
      },
      {
        id: 'equipe',
        rf: '08E.2',
        title: 'Equipe Ideal',
        desc: 'Composição recomendada de pedreiros, serventes, etc. por fase.',
      },
      {
        id: 'pedreiro',
        rf: '08E.3',
        title: 'Custo do Pedreiro',
        desc: 'Diária × mensalista CLT × empreiteiro, no período da obra.',
      },
      {
        id: 'aditivos',
        rf: '08E.4',
        title: 'Detector de Aditivos',
        desc: 'Checklist de red flags na contratação, com score de risco.',
      },
    ],
  },
  {
    moduloId: 'tributos',
    modulo: 'Tributos · Obra Certa',
    rf: 'RF-08F',
    items: [
      {
        id: 'impostos',
        rf: '08F.1',
        title: 'Impostos Totais',
        desc: 'ITBI + INSS + IPTU + ISSQN + cartório. Donut de tributos.',
      },
      {
        id: 'inss',
        rf: '08F.2',
        title: 'INSS de Obra',
        desc: 'Estimativa por tipo de contratação e estratégias de redução.',
      },
      {
        id: 'cno',
        rf: '08F.3',
        title: 'Alerta de CNO',
        desc: 'Countdown dos 30 dias após o alvará e checklist do cadastro.',
      },
      {
        id: 'habitese',
        rf: '08F.4',
        title: 'Habite-se',
        desc: 'Taxas, certidões e custo de regularização final.',
      },
    ],
  },
  {
    moduloId: 'execucao',
    modulo: 'Execução',
    rf: 'RF-08G',
    items: [
      {
        id: 'cronograma',
        rf: '08G.1',
        title: 'Cronograma Físico-Financeiro',
        desc: 'Gantt simplificado das 7 fases com % de desembolso por mês.',
      },
      {
        id: 'anti-retrabalho',
        rf: '08G.2',
        title: 'Identificador anti-retrabalho',
        desc: '12 erros de sequência. Marque prevenidos e riscos antes de cada fase.',
      },
      {
        id: 'atrasos',
        rf: '08G.3',
        title: 'Detector de Atrasos',
        desc: 'Variância plano × real por fase. Dispara alerta cascata.',
      },
      {
        id: 'manta-telhado',
        rf: '08G.4',
        title: 'Manta vs Telhado',
        desc: 'Questionário decisor para coberturas modernas × tradicionais.',
      },
    ],
  },
]

const TOTAL_RF08 = 28
const totalDisponiveis = TOTAL_RF08

export function CalculadorasPage() {
  const navigate = useNavigate()

  const handleNavigateToCalc = useCallback(
    (id: string) => {
      navigate(calcDetailPath(id))
    },
    [navigate],
  )

  return (
    <PageShell>
      <ModePageHeader
        title={GUIA_LABELS.ferramentasIndex}
        subtitle={GUIA_LABELS.ferramentasIndexSubtitle}
      />

      <Alert
        variant='success'
        title='Todas integradas ao guia'
        message={GUIA_LABELS.ferramentasIndexAlert}
      />

      <div className='cf3-calc-stats'>
        <div className='cf3-calc-stats__item'>
          <span className='cf3-calc-stats__label'>Disponíveis</span>
          <span className='cf3-calc-stats__val'>
            {totalDisponiveis}
            <span className='cf3-calc-stats__sub'>/{TOTAL_RF08}</span>
          </span>
        </div>
        <div className='cf3-calc-stats__item'>
          <span className='cf3-calc-stats__label'>Áreas do guia</span>
          <span className='cf3-calc-stats__val'>{CALC_GRUPOS.length}</span>
        </div>
      </div>

      {CALC_GRUPOS.map((grupo, grupoIdx) => (
        <section key={grupo.moduloId} className='cf3-block'>
          <div className='cf3-calc-grupo-head'>
            <span
              className={`cf3-calc-grupo-head__num cf3-calc-grupo-head__num--${grupo.moduloId}`}
            >
              {String(grupoIdx + 1).padStart(2, '0')}
            </span>
            <h3 className='cf3-calc-grupo-head__title'>{grupo.modulo}</h3>
          </div>

          <div className='cf3-calc-grupo-items'>
            {grupo.items.map((item) => (
              <Card
                key={item.id}
                surface='raised'
                clickable
                className='cf3-calc-item'
                onClick={() => handleNavigateToCalc(item.id)}
              >
                <div className='cf3-calc-item__body'>
                  <div>
                    <span className='cf3-badge cf3-badge--ferramenta'>
                      {GUIA_LABELS.ferramentaBadge}
                    </span>
                    <p className='cf3-calc-item__title'>{item.title}</p>
                    <p className='cf3-calc-item__desc'>{item.desc}</p>
                  </div>
                  <span className='cf3-calc-item__chevron' aria-hidden>
                    ›
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </PageShell>
  )
}
